/**
 * Өдрийн борлуулалт — салбар × нийлүүлэгч (`/accounting/sales-daily`).
 *
 * Буцаалт БУЦСАН ӨДӨРТ нь хасагдана (шийдвэр 2026-10-15): өнгөрсөн өдрийн
 * тоо хэзээ ч өөрчлөгдөхгүй, кассын хаалттай таарна.
 */

import { useQuery } from '@tanstack/react-query';
import { formatDate, formatMnt, money } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { DataTable, type Column } from '../data-table';
import { Section } from '../section';
import { StatCard, StatGrid } from '../stat-card';
import { Empty, ErrorNote } from '../ui';
import { AccountingFilters, ExcelButton, saveTableXlsx, useAccountingFilter } from './shared';

interface Row {
  date: string;
  storeId: string;
  storeName: string;
  supplierId: string;
  supplierName: string;
  soldUnits: number;
  returnedUnits: number;
  salesMnt: string;
  returnsMnt: string;
  netMnt: string;
}
type Totals = Pick<Row, 'soldUnits' | 'returnedUnits' | 'salesMnt' | 'returnsMnt' | 'netMnt'>;

/** Салбараар эсвэл нийлүүлэгчээр нэгтгэнэ — дэд нийлбэр. */
function rollup(rows: Row[], by: 'store' | 'supplier'): (Totals & { name: string })[] {
  const map = new Map<string, Totals & { name: string }>();
  for (const r of rows) {
    const name = by === 'store' ? r.storeName : r.supplierName;
    const t = map.get(name) ?? { name, soldUnits: 0, returnedUnits: 0, salesMnt: '0', returnsMnt: '0', netMnt: '0' };
    map.set(name, {
      name,
      soldUnits: t.soldUnits + r.soldUnits,
      returnedUnits: t.returnedUnits + r.returnedUnits,
      salesMnt: money(t.salesMnt).plus(r.salesMnt).toFixed(2),
      returnsMnt: money(t.returnsMnt).plus(r.returnsMnt).toFixed(2),
      netMnt: money(t.netMnt).plus(r.netMnt).toFixed(2),
    });
  }
  return [...map.values()].sort((a, b) => money(b.netMnt).comparedTo(money(a.netMnt)));
}

const amountCols = <T extends Totals>(): Column<T>[] => [
  { key: 'sold', header: 'Зарсан', align: 'right', cell: (r) => r.soldUnits },
  { key: 'sales', header: 'Борлуулалт', align: 'right', nowrap: true, cell: (r) => formatMnt(r.salesMnt) },
  { key: 'ret', header: 'Буцсан', align: 'right', cell: (r) => r.returnedUnits || '—' },
  {
    key: 'returns', header: 'Буцаалт', align: 'right', nowrap: true,
    cell: (r) => (money(r.returnsMnt).isZero() ? '—' : <span className="text-alert-ink">−{formatMnt(r.returnsMnt)}</span>),
  },
  { key: 'net', header: 'Цэвэр', align: 'right', nowrap: true, mobile: 'value', cell: (r) => <b>{formatMnt(r.netMnt)}</b> },
];

export function SalesDailyTab() {
  const f = useAccountingFilter();
  const q = useQuery({
    queryKey: ['accounting', 'sales-daily', f.query()],
    queryFn: () => api<Envelope<Row[]> & { meta: { totals: Totals } }>(`/accounting/sales-daily?${f.query()}`),
  });
  const rows = q.data?.data ?? [];
  const totals = q.data?.meta.totals;

  const excel = () =>
    saveTableXlsx({
      file: `borluulalt-${f.from}-${f.to}.xlsx`,
      title: 'Өдрийн борлуулалт — салбар, нийлүүлэгч',
      period: `${f.from} — ${f.to}`,
      headers: ['Огноо', 'Салбар', 'Нийлүүлэгч', 'Зарсан', 'Борлуулалт ₮', 'Буцсан', 'Буцаалт ₮', 'Цэвэр ₮'],
      widths: [12, 22, 22, 9, 15, 9, 15, 15],
      rows: [
        ...rows.map((r) => [
          r.date, r.storeName, r.supplierName, r.soldUnits, { money: r.salesMnt },
          r.returnedUnits, { money: r.returnsMnt }, { money: r.netMnt },
        ]),
        ...(totals
          ? [['Нийт', null, null, totals.soldUnits, { money: totals.salesMnt }, totals.returnedUnits,
              { money: totals.returnsMnt }, { money: totals.netMnt }]]
          : []),
      ],
    });

  return (
    <div className="flex flex-col gap-3.5">
      <Section
        title="Өдрийн борлуулалт"
        subtitle="салбар, нийлүүлэгч тус бүрээр"
        note="Буцаалт буцсан өдөртөө хасагдана. Дүн нь худалдан авагчийн бодитоор төлсөн мөнгө."
        actions={<ExcelButton onClick={excel} disabled={rows.length === 0} />}
      >
        <AccountingFilters f={f} supplier />
        {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
        {totals ? (
          <StatGrid>
            <StatCard label="Борлуулалт" value={formatMnt(totals.salesMnt)} footer={`${totals.soldUnits} ширхэг`} />
            <StatCard label="Буцаалт" value={formatMnt(totals.returnsMnt)} footer={`${totals.returnedUnits} ширхэг`} />
            <StatCard label="Цэвэр борлуулалт" value={formatMnt(totals.netMnt)} />
          </StatGrid>
        ) : null}
        <DataTable
          rows={rows}
          loading={q.isPending}
          rowKey={(r) => `${r.date}|${r.storeId}|${r.supplierId}`}
          empty={<Empty>Энэ хугацаанд борлуулалт, буцаалт алга</Empty>}
          columns={[
            { key: 'date', header: 'Огноо', nowrap: true, mobile: 'title', cell: (r) => formatDate(r.date) },
            { key: 'store', header: 'Салбар', cell: (r) => r.storeName },
            { key: 'supplier', header: 'Нийлүүлэгч', cell: (r) => r.supplierName },
            ...amountCols<Row>(),
          ]}
        />
      </Section>

      {rows.length > 0 ? (
        <div className="grid gap-3.5 lg:grid-cols-2">
          {(['store', 'supplier'] as const).map((by) => (
            <Section key={by} title={by === 'store' ? 'Салбараар' : 'Нийлүүлэгчээр'}>
              <DataTable
                rows={rollup(rows, by)}
                rowKey={(r) => r.name}
                columns={[{ key: 'name', header: by === 'store' ? 'Салбар' : 'Нийлүүлэгч', mobile: 'title', cell: (r) => r.name }, ...amountCols()]}
              />
            </Section>
          ))}
        </div>
      ) : null}
    </div>
  );
}
